import { useMemo, useState } from 'react';

import {
  formatAgentName,
  type AgentFormValues,
  type StoreData,
  validateAgents,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_AGENTS,
} from '~domains/partner-application/shared/constants';
import { getApplicationEventProperties } from '~domains/partner-application/shared/lib';
import { ItemList } from '~domains/partner-application/shared/ui/item-list';

import { ampli } from '~shared/analytics';
import { useConfirmation } from '~shared/ui/confirmation';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Button } from '@breezeventures/ui-kit/button';
import { Icon } from '@breezeventures/ui-kit/next/icon';
import { Typography } from '@breezeventures/ui-kit/typography';

type AgentsListProps = {
  applicationId?: string;
  agents: AgentFormValues[] | undefined;
  pointsOfSales: StoreData[] | undefined;
  onBack: () => void;
  onClose: () => void;
  onSubmit: () => void;
  onEditAgent: (agentId: string) => void;
  onAddAgent: () => void;
  onRemoveAgent: (agentId: string) => Promise<void>;
};

export const AgentsList = ({
  applicationId,
  agents,
  pointsOfSales,
  onBack,
  onClose,
  onSubmit,
  onEditAgent,
  onAddAgent,
  onRemoveAgent,
}: AgentsListProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { confirm } = useConfirmation();

  const agentErrors = useMemo(() => {
    const errors = validateAgents(agents ?? [], { pointsOfSales });
    if (errors.general) {
      return {};
    }

    return errors;
  }, [agents, pointsOfSales]);

  const items =
    agents?.map((agent) => {
      const errors = agentErrors[agent.uiId];
      const errorText = errors?.length
        ? "This agent's info is incomplete. Update or delete the agent."
        : undefined;

      return {
        id: agent.uiId,
        primaryText: formatAgentName(agent),
        secondaryText: agent.email,
        ...(errorText && { errorText }),
      };
    }) ?? [];

  const handleRemoveAgent = async (agentId: string) => {
    try {
      await confirm({
        title: 'Are you sure you want to delete this agent?',
        description:
          'All information for this agent will be permanently removed.',
        confirmButtonText: 'Yes, delete the agent',
      });
      ampli.mpPaDeleteAgentDialogConfirmClick(
        getApplicationEventProperties(applicationId)
      );
    } catch {
      ampli.mpPaDeleteAgentDialogCancelClick(
        getApplicationEventProperties(applicationId)
      );
      return;
    }

    try {
      setIsUpdating(true);
      await onRemoveAgent(agentId);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Screen
      header={<ScreenHeader onBack={onBack} onClose={onClose} />}
      footer={
        <FooterWithProgressBar
          currentStepIndex={STEP_INDEX_AGENTS}
          totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
          onSubmit={onSubmit}
          isLoading={isUpdating}
        />
      }
    >
      <div className="flex flex-col gap-3 py-6">
        <Typography.Title size="s">List of sales agents</Typography.Title>
        <Typography.Text size="m" color="secondary">
          Fill in the details for each sales agent and their assigned store.
        </Typography.Text>
      </div>

      <ItemList
        items={items}
        onEdit={onEditAgent}
        onRemove={handleRemoveAgent}
      />

      <Button
        type="button"
        size="l"
        variant="secondary"
        width="full"
        onClick={onAddAgent}
      >
        <Icon name="plus" size={24} version="v1" />
        Add agent
      </Button>
    </Screen>
  );
};
